import { toString } from '@studio/js-utils/logger';
import { useEffect, type ReactElement } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Bulletin } from '#~/error/components/bulletin/index.js';
import { DefaultMessage, NotFoundError, OfflineError } from '#~/error/index.js';
import { sentry } from '#~/logger/sentry/index.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { BootErrorScreen } from '#~/routes/boot-overlay/boot-error-screen.js';
import { bootFailureCopy, retryCurrentDocumentBoot } from '#~/routes/root/bootstrap-analytics.js';

/** Error boundary `<Fallback>`. Branded copy lives here because overlay is unmounted. */
export const Fallback = ({ error }: FallbackProps): ReactElement => {
  const isOffline = error instanceof OfflineError;
  const isNotFound = error instanceof NotFoundError;
  const kaboom = !isNotFound && !isOffline;

  useEffect(() => {
    if (!kaboom) {
      return;
    }
    sentry.logEvent(error instanceof Error ? error : (toString(error) ?? DefaultMessage));
  }, [kaboom, error]);

  if (isOffline) {
    return (
      <Bulletin
        icon='alert-circle'
        message={error.message}
        {...STagNew(Sel.BootOverlay, 'boundary')}
      />
    );
  }

  if (isNotFound) {
    return (
      <Bulletin
        icon='alert-circle'
        message={toString(error) ?? DefaultMessage}
        {...STagNew(Sel.BootOverlay, 'boundary')}
      />
    );
  }

  const copy = bootFailureCopy(error);
  return (
    <BootErrorScreen
      source='error-boundary'
      error={{
        message: copy.message,
        detail: copy.detail,
        severity: 'retryable',
        retryFn: () => retryCurrentDocumentBoot()
      }}
    />
  );
};
